/**
 * Lightweight external stores for the single-instance visualizer dock.
 *
 * We deliberately avoid a new Redux slice here for the dock wiring:
 *   - dockStateStore : { activeSlotName, dockMode }  (which slot the shell overlays)
 *   - dockRectStore  : the measured DOMRect of the active slot (animated)
 *   - the single VisualizerAdapter instance (imperative handle used by visualizer.js)
 *
 * The canvas *input* (layers/layout/canvasId/size) is NOT stored here anymore.
 * It's derived centrally from Redux + the layout singleton by
 * `visualizerNew/useDockedVisualizerInput.js` — the v3 useVisualizerLayers analog —
 * so there is a single reader/writer instead of three racing page feeders.
 */

function createStore(initialValue, isEqual) {
  let value = initialValue;
  const listeners = new Set();

  const getSnapshot = () => value;

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const set = (next) => {
    const resolved = typeof next === "function" ? next(value) : next;
    if (isEqual && isEqual(value, resolved)) return;
    value = resolved;
    listeners.forEach((l) => l());
  };

  return { getSnapshot, subscribe, set };
}

const rectEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  );
};

/** { activeSlotName, dockMode: 'fullscreen' | 'embedded' } */
export const dockStateStore = createStore({
  activeSlotName: "qkb-visualizer",
  dockMode: "fullscreen",
});

/** { top, left, width, height } | null */
export const dockRectStore = createStore(null, rectEqual);

/**
 * { [slotName]: { enableLoader, disableLoader } } — per-slot canvas-loader
 * callbacks. Each page's <DockCanvas/> registers its own loader pair here
 * (e.g. materials' setMaterialsCanvasLoading); useDockedVisualizerInput()
 * reads back whichever slot is currently active and hands that pair to the
 * single docked canvas instance, so the right page's loading state gets
 * toggled no matter which slot is on screen.
 */
export const dockLoadersStore = createStore({});

export const setSlotLoader = (slotName, loader) => {
  dockLoadersStore.set((prev) => {
    if (!loader) {
      if (!(slotName in prev)) return prev;
      const next = { ...prev };
      delete next[slotName];
      return next;
    }
    return { ...prev, [slotName]: loader };
  });
};

/* ---- the single imperative VisualizerAdapter instance ---- */
let visualizerInstance = null;
const instanceListeners = new Set();

export const setVisualizerInstance = (inst) => {
  visualizerInstance = inst;
  instanceListeners.forEach((l) => l());
};
export const getVisualizerInstance = () => visualizerInstance;
export const subscribeVisualizerInstance = (l) => {
  instanceListeners.add(l);
  return () => instanceListeners.delete(l);
};

/* ---- route → slot/mode config (HashRouter) ---- */
export const routeConfig = {
  "/visualizer": { slot: "qkb-visualizer", mode: "fullscreen" },
  // "/": { slot: "qkb-visualizer", mode: "fullscreen" }, // commented out for now,
  // Rollout targets (add matching <DockSlot/>s on these pages):
  "/materials": { slot: "qkb-materials", mode: "embedded" },
  "/shoppingcart": { slot: "qkb-cart", mode: "embedded" },
};

export const applyRoute = (pathname) => {
  const match = Object.entries(routeConfig).find(([route]) =>
    pathname === route || pathname.startsWith(route + "/")
  );

  // Routes with no entry above (e.g. /quote-design) aren't part of the docked
  // rollout and render no matching <DockSlot/>. Explicitly hide the dock for
  // them instead of leaving the previous slot/mode in place — otherwise the
  // last-active slot (or the "qkb-visualizer"/fullscreen default on a fresh
  // page load) stays active, and since the always-mounted legacy visualizer
  // tree keeps a "qkb-visualizer" <DockSlot/> registered on every route, the
  // docked canvas stays visible even on pages that never asked to show it.
  const config = match ? match[1] : { slot: null, mode: dockStateStore.getSnapshot().dockMode };

  const current = dockStateStore.getSnapshot();
  if (
    current.activeSlotName === config.slot &&
    current.dockMode === config.mode
  ) {
    return;
  }

  dockStateStore.set({
    activeSlotName: config.slot,
    dockMode: config.mode,
  });
};
